import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Target, 
  Calendar, 
  BookOpen,
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Languages
} from "lucide-react";
import { useGeneratePlan } from "@/hooks/use-plans";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface Strength {
  rank: number;
  name: string;
  domain: string;
}

interface PersonalizedPlanGeneratorProps {
  topStrengths: Strength[];
  userId: string;
  planDuration?: 3 | 6; // months
}

export default function PersonalizedPlanGenerator({
  topStrengths,
  userId,
  planDuration = 6,
}: PersonalizedPlanGeneratorProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [expertise, setExpertise] = useState("");
  const [language, setLanguage] = useState<string>(i18n.language);
  const { mutate: generatePlan, isPending, isSuccess, error } = useGeneratePlan();

  const handleGeneratePlan = () => {
    if (!expertise.trim()) {
      toast({
        title: t('common.error'),
        description: t('plans.expertiseRequired') || 'Please enter your expertise',
        variant: "destructive",
      });
      return;
    }

    generatePlan(
      {
        language: language,
        expertise: expertise.trim(),
      },
      {
        onSuccess: () => {
          toast({
            title: t('plans.planGenerated') || 'Plan Generated Successfully',
            description: t('plans.planGeneratedDesc') || 'Your personalized development plan has been created',
          });
        },
        onError: (error: any) => {
          toast({
            title: t('common.error'),
            description: getLocalizedErrorMessage(error, 'plans.generateError'),
            variant: "destructive",
          });
        },
      }
    );
  };

  // Loading State with Modern Progress
  if (isPending) {
    return (
      <Card className="p-6 bg-white dark:bg-card/50 backdrop-blur-sm border-border/50 sm:p-12">
        <div className="max-w-2xl mx-auto space-y-8 text-center">
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
            <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
            <Sparkles className="absolute inset-0 m-auto w-10 h-10 text-primary animate-pulse" />
          </div>
          
          <div className="space-y-3">
            <h2 className="text-2xl font-bold">{t('plans.generating')}</h2>
            <p className="text-muted-foreground">
              {t('plans.generatingDesc') || 'AI is analyzing your strengths and creating a personalized plan...'}
            </p>
          </div>

          {/* Animated Progress Steps */}
          <div className="space-y-3 text-start max-w-md mx-auto">
            {[
              t('plans.step1') || 'Analyzing your top strengths',
              t('plans.step2') || 'Identifying development opportunities',
              t('plans.step3') || 'Creating personalized milestones',
            ].map((step, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-3 p-3 bg-accent/30 rounded-lg animate-pulse"
                style={{ animationDelay: `${idx * 0.2}s` }}
              >
                <Loader2 className="w-4 h-4 text-primary animate-spin" />
                <span className="text-sm">{step}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  // Success State
  if (isSuccess) {
    return (
      <Card className="p-6 bg-white dark:bg-card/50 backdrop-blur-sm border-border/50 sm:p-12">
        <div className="max-w-2xl mx-auto space-y-6 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-500/10 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{t('plans.planGenerated') || 'Plan Generated Successfully!'}</h2>
            <p className="text-muted-foreground">
              {t('plans.planGeneratedDesc') || 'Your personalized development plan has been created'}
            </p>
          </div>

          <Button 
            size="lg" 
            onClick={() => window.location.reload()}
            className="gap-2"
          >
            <Target className="w-5 h-5" />
            {t('plans.viewMyPlans') || 'View My Plans'}
          </Button>
        </div>
      </Card>
    );
  }

  // Error State
  if (error) {
    return (
      <Card className="p-6 bg-white dark:bg-card/50 backdrop-blur-sm border-border/50 sm:p-12">
        <div className="max-w-2xl mx-auto space-y-6 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-destructive" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-2xl font-bold">{t('common.error')}</h2>
            <p className="text-muted-foreground">
              {getLocalizedErrorMessage(error, 'plans.generateError')}
            </p>
          </div>

          <Button 
            size="lg" 
            onClick={() => setExpertise("")}
            variant="outline"
            className="gap-2"
          >
            <Sparkles className="w-5 h-5" />
            {t('common.tryAgain') || 'Try Again'}
          </Button>
        </div>
      </Card>
    );
  }

  // Initial Form State
  return (
      <Card className="p-4 bg-white dark:bg-card/50 backdrop-blur-sm border-border/50 sm:p-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-10 h-10 text-primary" />
          </div>
          
          <div className="text-center">
            <h2 className="text-xl font-bold mb-2 sm:text-2xl">{t('plans.generateTitle')}</h2>
            <p className="text-muted-foreground">
              {t('plans.generateDesc', { duration: planDuration })}
            </p>
          </div>

          {/* Language Selection */}
          <div className="space-y-3 text-start">
            <Label htmlFor="language" className="text-base font-semibold flex items-center gap-2">
              <Languages className="w-4 h-4" />
              {t('plans.planLanguage') || 'Plan Language'}
            </Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger id="language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="ar">العربية (Arabic)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('plans.languageHelp') || 'The AI will generate the plan in your selected language'}
            </p>
          </div>

          {/* Expertise Input */}
          <div className="space-y-3 text-start">
            <Label htmlFor="expertise" className="text-base font-semibold">
              {t('plans.yourExpertise') || 'Your Expertise / Role'}
            </Label>
            <Input
              id="expertise"
              type="text"
              placeholder={t('plans.expertisePlaceholder') || 'e.g., Project Manager, Software Engineer, Marketing Lead...'}
              value={expertise}
              onChange={(e) => setExpertise(e.target.value)}
              className="text-base text-start"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && expertise.trim()) {
                  handleGeneratePlan();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              {t('plans.expertiseHelp') || 'This helps AI tailor the plan to your professional context'}
            </p>
          </div>

          <div className="grid gap-4 text-start">
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Target className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">{t('plans.feature1Title')}</p>
                <p className="text-xs text-muted-foreground">{t('plans.feature1Desc')}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Calendar className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">{t('plans.feature2Title')}</p>
                <p className="text-xs text-muted-foreground">{t('plans.feature2Desc')}</p>
              </div>
            </div>
          </div>
          
          <Button 
            size="lg" 
            onClick={handleGeneratePlan} 
            disabled={!expertise.trim()}
            className="gap-2 w-full"
          >
            <Sparkles className="w-5 h-5" />
            {t('plans.generateButton')}
          </Button>
        </div>
      </Card>
    );
  };
